import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Dialog, Form, Input, Loading, Radio, Select, Textarea } from 'tdesign-react';
import { UNSAFE_bugSetFieldsValue, isVoid, toTsOptions } from '@/utils/common';
import { createGeo, getGeoDetail, getGeoParams, updateGeo } from '@/services/clusterService';
import Text from '@/components/Text';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';

const { FormItem } = Form;

function getToken(jsonStr) {
  try {
    return JSON.parse(jsonStr)?.token || '';
  } catch (error) {
    return '';
  }
}

function ModalForm({ visible, handleClose, allList, geoList, remoteClusterName }) {
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const confirmBtn = useDisableButton({ loading: submitting, content: '确定' });

  const isEdit = !!remoteClusterName;
  const clusterName = searchParams.get('name');

  // 剔除自己、非活跃、已关联GEO的集群
  const options = useMemo(() => {
    const aliveList = allList.filter((item) => item.clusterStatus === 'live');

    const notGeo = aliveList.filter(
      (item) => ![...geoList, clusterName].includes(item.clusterName),
    );

    const _options = notGeo.map((item) => ({
      label: item.clusterName,
      value: item.clusterName,
    }));

    // 修改时，带上修改的geo集群自身
    if (remoteClusterName) {
      return [{ label: remoteClusterName, value: remoteClusterName }, ..._options];
    }

    return _options;
  }, [allList, geoList, clusterName, remoteClusterName]);

  const pulsarAuthType = Form.useWatch('pulsarAuthType', form);

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      setSubmitting(true);
      try {
        const parmas = {
          ...fields,
          originClusterName: clusterName,
          pulsarAuthParams: fields.pulsarAuthParams
            ? JSON.stringify({ token: fields.pulsarAuthParams })
            : fields.pulsarAuthParams,
        };
        // 修改
        if (remoteClusterName) {
          await updateGeo(parmas);
        } else {
          await createGeo(parmas);
        }
        handleClose(true);
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };

  const doInit = useMemoizedFn(async () => {
    if (remoteClusterName) {
      setLoading(true);
      const { data } = await getGeoDetail({
        clusterName,
        remoteClusterName,
      });
      setLoading(false);
      const values = {
        remoteClusterName,
        remoteAdminUrl: data.serviceUrl,
        remoteBrokerServiceUrl: data.brokerServiceUrl,
        pulsarAuthType: isVoid(data.authenticationParameters) ? 'NONE' : 'JWT',
        pulsarAuthParams: getToken(data.authenticationParameters),
      };

      UNSAFE_bugSetFieldsValue(form, values);
    }
  });

  useEffect(() => {
    if (visible) {
      doInit();
    } else {
      form.reset();
    }
  }, [visible, form, doInit]);

  const remoteClusterChange = async (value) => {
    if (value) {
      const { data } = await getGeoParams({
        clusterName,
        remoteClusterName: value,
      });
      UNSAFE_bugSetFieldsValue(form, {
        remoteAdminUrl: data.remoteAdminUrl,
        remoteBrokerServiceUrl: data.remoteBrokerServiceUrl,
        pulsarAuthType: data.pulsarAuthType,
        pulsarAuthParams: getToken(data.pulsarAuthParams),
      });
    }
  };

  return (
    <Dialog
      onConfirm={() => form.submit()}
      confirmBtn={confirmBtn}
      visible={visible}
      header={isEdit ? '修改GEO集群' : '添加GEO集群'}
      width={550}
      onClose={() => handleClose()}
    >
      <Loading loading={loading} size='small'>
        <Form form={form} style={{ width: '90%' }} labelWidth={180} onSubmit={handleSubmit}>
          <FormItem label='集群名称' name='remoteClusterName' rules={[{ required: true }]}>
            {isEdit ? (
              <Text />
            ) : (
              <Select options={toTsOptions(options)} onChange={remoteClusterChange} />
            )}
          </FormItem>
          <FormItem label='BrokerAdminURL' name='remoteAdminUrl' rules={[{ required: true }]}>
            <Input />
          </FormItem>
          <FormItem
            label='BrokerServiceURL'
            name='remoteBrokerServiceUrl'
            rules={[{ required: true }]}
          >
            <Input />
          </FormItem>
          <FormItem name='pulsarAuthType' label='权限信息' rules={[{ required: true }]}>
            <Radio.Group>
              <Radio value='JWT'>JWT</Radio>
              <Radio value='NONE'>NONE</Radio>
            </Radio.Group>
          </FormItem>

          {pulsarAuthType === 'JWT' && (
            <FormItem rules={[{ required: true }]} name='pulsarAuthParams' label='Pulsar JWT'>
              <Textarea placeholder='请输入' />
            </FormItem>
          )}
        </Form>
      </Loading>
    </Dialog>
  );
}

export default connect((state) => ({
  geoList: state.cluster.geoList,
  allList: state.cluster.allList,
}))(ModalForm);
